/* -----------------Third parties--------------- */
import { List, type ListItemProps, Text } from "react-native-paper";

import type { ViewStyle } from "react-native";
import type { Style } from "react-native-paper/lib/typescript/components/List/utils";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import Button from "../Button";
import CustomImage from "../CustomImage";
/* -----------------Child components--------------- */
import AudioPlaybackTestSwitch from "./AudioPlaybackTestSwitch";
import DarkModeSwitch from "./DarkModeSwitch";
import { ExamContextListItem } from "./ExamContextListItem";
import PaywallSwitch from "./PaywallSwitch";
/* -----------------Props--------------- */
type Props = {
	type?: string;
	icon: string;
	options?: {
		title: string;
		description: string;
	}[];
	iconRight?: string;
	rightText?: string;
	isDarkMode: boolean;
	showAudioPlaybackTestDialog: boolean;
	adminPaywallEnabled?: boolean;
	accountExtendValidity: string;
	hasMoreThanOneExam?: boolean;
	toggleDarkMode: () => void;
	toggleAudioPlaybackTestDialog: () => void;
	toggleAdminPaywall?: () => void;
	handleNavigateToExamsRoute: () => void;
} & ListItemProps;

/* -----------------Component--------------- */
const AccountListSection = (props: Props) => {
	const {
		type,
		title,
		description,
		icon,
		iconRight,
		rightText,
		isDarkMode,
		showAudioPlaybackTestDialog,
		adminPaywallEnabled,
		accountExtendValidity,
		hasMoreThanOneExam,
		onPress,
		toggleDarkMode,
		toggleAudioPlaybackTestDialog,
		toggleAdminPaywall,
		handleNavigateToExamsRoute,
	} = props;

	const theme = useAppTheme();

	const renderLeft = (p: { color: string; style: Style }) => {
		if (icon.includes(".")) {
			return (
				<CustomImage
					source={{ uri: icon }}
					contentFit="contain"
					style={[
						p.style,
						{
							width: 56,
							height: 56,
							borderRadius: theme.roundness * 3,
						},
					]}
				/>
			);
		}
		return (
			<List.Icon
				{...p}
				icon={(props) => <AppIcon {...props} name={icon as IconType} />}
			/>
		);
	};
	const renderRight = (propsRight: {
		color: string;
		style?: ViewStyle;
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: render function with multiple branches
	}) => {
		if (type === "toggle") {
			return (
				<DarkModeSwitch
					isDarkMode={isDarkMode}
					toggleDarkMode={toggleDarkMode}
				/>
			);
		}
		if (type === "toggle-audio-playback") {
			return (
				<AudioPlaybackTestSwitch
					showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
					toggleAudioPlaybackTestDialog={toggleAudioPlaybackTestDialog}
				/>
			);
		}
		if (type === "toggle-paywall" && toggleAdminPaywall !== undefined) {
			return (
				<List.Icon
					{...propsRight}
					icon={() => (
						<PaywallSwitch
							adminPaywallEnabled={adminPaywallEnabled ?? false}
							toggleAdminPaywall={toggleAdminPaywall}
						/>
					)}
				/>
			);
		}
		if (type === "extend-subscription") {
			return (
				<List.Icon
					{...propsRight}
					icon={() => {
						return (
							<Button mode="text" onPress={onPress}>
								{accountExtendValidity}
							</Button>
						);
					}}
				/>
			);
		}
		if (rightText || iconRight) {
			return (
				<Box flexDirection="row" alignItems="center" gap={12}>
					{rightText ? <Text variant="bodyMedium">{rightText}</Text> : null}
					{iconRight ? (
						<List.Icon
							{...propsRight}
							icon={(props) => (
								<AppIcon {...props} name={iconRight as IconType} />
							)}
						/>
					) : null}
				</Box>
			);
		}

		return null;
	};

	const RenderListitem = () => (
		<List.Item
			title={title}
			description={description}
			onPress={onPress}
			style={{
				paddingLeft: 0,
				paddingRight: 16,
			}}
			left={renderLeft}
			right={renderRight}
		/>
	);
	switch (type) {
		case "exam-filter":
			return (
				<ExamContextListItem
					title={title as string}
					icon={icon}
					hasMoreThanOneExam={hasMoreThanOneExam}
					handleNavigateToExamsRoute={handleNavigateToExamsRoute}
				/>
			);
		default:
			return <RenderListitem />;
	}
};

export default AccountListSection;
